<template>
  <Field
    v-bind="{ inputId, overrideValue, errors, forceError }"
    ref="fieldRef"
    v-slot="{ hasError, errors, inputId, updateModelValue }"
  >
    <Combobox
      v-model="selected"
      :multiple="false"
      @update:model-value="onChange($event, updateModelValue)"
    >
      <ComboboxLabel
        v-if="label"
        class="vp-label"
        :for="inputRef?.el?.id"
      >
        {{ label }}
      </ComboboxLabel>

      <div class="relative">
        <div
          class="flex gap-2 items-center w-full cursor-default overflow-hidden rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm hover:bg-vp-base-200 focus-within:bg-vp-base-200"
        >
          <ComboboxInput
            ref="inputRef"
            class="w-full bg-vp-base-100 hover:bg-vp-base-200 focus:bg-vp-base-200 py-2 pl-1 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
            :display-value="() => displayValue"
            :placeholder="placeholder"
            @change="query = $event.target.value"
          />
          <div
            v-if="props.optionsLoading"
            class="spinner border-t-2 border-vp-pink rounded-full w-4 h-4 animate-spin"
          />
          <ComboboxButton class="relative inset-y-0 right-0 flex items-center pr-2">
            <iconify-icon
              icon="mdi:chevron-down"
              class="h-5 w-5 text-gray-400 text-lg"
            />
          </ComboboxButton>
        </div>

        <TransitionRoot
          leave="transition ease-in duration-100"
          leave-from="opacity-100"
          leave-to="opacity-0"
          @after-leave="query = ''"
        >
          <ComboboxOptions
            class="absolute mt-1 max-h-52 w-full overflow-auto shadow-sm rounded-md bg-white py-1 text-base sm:text-sm z-10"
          >
            <div
              v-if="filteredOptions.length === 0 && query !== '' && !newOption"
              class="relative cursor-default select-none px-4 py-2 text-gray-700"
            >
              {{ $gettext('Nothing found') }}
            </div>

            <ComboboxOption
              v-for="option in filteredOptions"
              :key="option.value"
              v-slot="{ selected, active }"
              as="template"
              :value="option"
            >
              <li
                class="relative cursor-default select-none py-2 pl-10 pr-4"
                :class="{
                  'bg-vp-primary text-white': active,
                  'text-gray-900': !active,
                }"
              >
                <slot
                  name="item"
                  v-bind="{ selected, active, option }"
                >
                  <span
                    class="block truncate"
                    :class="{ 'font-medium': selected, 'font-normal': !selected }"
                  >
                    {{ option.name }}
                  </span>
                </slot>
                <span
                  v-if="selected"
                  class="absolute inset-y-0 left-0 flex items-center pl-3"
                  :class="{ 'text-white': active, 'text-vp-primary': !active }"
                >
                  <iconify-icon
                    icon="mdi:check-bold"
                    class="h-5 w-5"
                  />
                </span>
              </li>
            </ComboboxOption>

            <ComboboxOption
              v-if="allowAdd && newOption"
              v-slot="{ active }"
              :value="newOption"
              as="template"
            >
              <li
                class="relative cursor-default select-none py-2 pl-10 pr-4"
                :class="{
                  'bg-vp-primary text-white': active,
                  'text-gray-900': !active,
                }"
              >
                {{ $gettext('Add "%{option}"', { option: newOption.name }) }}
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </TransitionRoot>
      </div>
    </Combobox>

    <span
      v-if="hasError"
      class="text-vp-error font-bold text-xs"
    >{{ errors.join(', ') }}</span>
  </Field>
</template>

<script setup lang="ts">
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  ComboboxButton,
  ComboboxLabel,
  TransitionRoot,
} from '@headlessui/vue';
import { ref, watch } from 'vue';
import { Field } from 'vuniform';
import { computed } from 'vue';

type Option = {
  value: string | unknown;
  name: string;
  __new?: boolean;
};

type Props = {
  inputId: string;
  options?: Option[];
  defaultValue?: string;
  clearable?: boolean;
  errors?: string[];
  forceError?: boolean;
  multiple?: boolean;
  placeholder?: string;
  clearText?: string;
  overrideValue?: string;
  label?: string;
  disabled?: boolean;
  allowAdd?: boolean;
  disableFilter?: boolean;
  optionsLoading?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  clearText: 'Clear',
  options: () => [],
  errors: () => [],
});

const emit = defineEmits<{
  (e: 'change', value?: Option): void;
  (e: 'clear'): void;
  (e: 'add', value: Option): void;
  (e: 'search', value: string): void;
}>();

const fieldRef = ref();
const inputRef = ref();
const selected = ref(props.options[0]);
const query = ref('');

const value = computed(() => fieldRef.value?.value);
const displayValue = computed(() => value.value && props.options.find(o => o.value == value.value)?.name);

watch(query, value => emit('search', value));

const newOption = computed(() => {
  if (props.allowAdd && query.value) {
    return {
      value: query.value,
      name: query.value,
      __new: true,
    };
  }

  return null;
});

const onChange = (selectedOption: Option, cb: (value?: unknown) => void) => {
  if (selectedOption.__new) {
    emit('add', selectedOption);

    return;
  }

  const selectedValue = selectedOption.value;
  if (cb) {
    cb(selectedValue);
  }
  emit('change', selectedValue);
};

const filteredOptions = computed(() => {
  if (props.disableFilter) {
    return props.options;
  }

  return query.value === ''
    ? props.options
    : props.options.filter(option =>
        option.name.toLowerCase().replace(/\s+/g, '').includes(query.value.toLowerCase().replace(/\s+/g, '')),
      );
});
</script>
